/**
 * Table connection: joins the Colyseus room, republishes state changes as
 * plain RoomState projections, and carries the command channel with
 * optimistic echo for OWN-token movement only (phase1-plan §3 — the one
 * latency-sensitive interaction; everything else waits for the server).
 *
 * The sdk client is injected so the wiring is unit-testable without a
 * websocket. The optimistic overlay lives here: `getState()` returns the
 * authoritative projection with pending own-moves applied on top, and
 * `getPendingCommandCount()` is the §6.2 quiescence hook.
 */

import { Client } from "@colyseus/sdk";
import {
  validateCommand,
  type Command,
  type CommandRejectionCode,
  type RoomState,
} from "@asarimitsuha/table-core";
import { TABLE_CORE_VERSION } from "@asarimitsuha/table-core";
import { projectRoomState } from "./stateProjection.js";

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed";

/** Custom close code the server uses for "your seat was removed by the GM"
 *  (test-spec E2E-13: a specific message, never a generic error). */
export const SEAT_REMOVED_CLOSE_CODE = 4710;

export interface ConnectOptions {
  endpoint: string;
  code: string;
  name: string;
  seatId?: string | undefined;
  /** Reclaim credential issued by the server with the seat (test-spec §5.1). */
  seatSecret?: string | undefined;
  gmKey?: string | undefined;
}

export interface SeatAssignment {
  seatId: string;
  seatSecret: string;
}

export interface CommandRejection {
  commandId: string | null;
  reason: CommandRejectionCode;
}

/** Payload of the server's "seatRemoved" room message, sent BEFORE the 4710
 *  close so the told-why notice can name who did it (§5.2 layer 3). */
export interface SeatRemovedInfo {
  removedByName: string | null;
}

export interface TableConnection {
  getState(): RoomState;
  getStatus(): ConnectionStatus;
  /** This browser's seat in the room (assigned by the server on join). */
  getSeatId(): string | null;
  /** Optimistic echoes not yet acknowledged (test-spec §6.2). */
  getPendingCommandCount(): number;
  /**
   * Validate locally (the SAME shared validator the server runs — CMD-09),
   * echo own-token moves optimistically, send. Returns the commandId, or
   * null when local validation already rejected (reported like a server
   * rejection).
   */
  sendCommand(command: Command): string | null;
  /** Skip local validation — E2E-05's injection path proving the SERVER
   *  refuses. Dev/test builds only reach this through window.__vtt. */
  sendRawCommand(command: unknown): string;
  onChange(listener: () => void): void;
  onRejection(listener: (rejection: CommandRejection) => void): void;
  /** Told-why channel: fires when the server closes the seat (removal),
   *  carrying who removed it when the server said so. */
  onSeatRemoved(listener: (info: SeatRemovedInfo) => void): void;
  leave(): Promise<void>;
}

/** The subset of the sdk Room the connection wires up. */
export interface RoomLike {
  onStateChange(cb: (state: { toJSON(): unknown }) => void): void;
  onMessage(type: string, cb: (message: never) => void): void;
  onLeave(cb: (code?: number) => void): void;
  /** Transient connection loss — the sdk starts auto-reconnecting. */
  onDrop(cb: (code?: number) => void): void;
  /** Auto-reconnect succeeded; state resyncs via onStateChange. */
  onReconnect(cb: () => void): void;
  send(type: string, payload: unknown): void;
  leave(consented?: boolean): Promise<unknown>;
}

export interface ConnectDeps {
  join(options: ConnectOptions): Promise<RoomLike>;
  /** Called when the server assigns/confirms this browser's seat. */
  onSeatAssigned?(assignment: SeatAssignment): void;
  newCommandId?(): string;
}

/** Wire options: only defined fields go on the join request. `coreVersion`
 *  always rides along — the server refuses a missing or mismatched one with
 *  `protocol_mismatch` (table-core join contract). */
export function buildJoinOptions(options: ConnectOptions): Record<string, string> {
  const joinOptions: Record<string, string> = {
    code: options.code,
    name: options.name,
    coreVersion: TABLE_CORE_VERSION,
  };
  if (options.seatId !== undefined) joinOptions["seatId"] = options.seatId;
  if (options.seatSecret !== undefined) joinOptions["seatSecret"] = options.seatSecret;
  if (options.gmKey !== undefined) joinOptions["gmKey"] = options.gmKey;
  return joinOptions;
}

export function sdkJoin(options: ConnectOptions): Promise<RoomLike> {
  const client = new Client(options.endpoint);
  return client.joinOrCreate(
    "table",
    buildJoinOptions(options),
  ) as unknown as Promise<RoomLike>;
}

export async function connectToTable(
  options: ConnectOptions,
  deps: ConnectDeps = { join: sdkJoin },
): Promise<TableConnection> {
  let status: ConnectionStatus = "connecting";
  let serverState = projectRoomState(null);
  let seatId: string | null = null;
  const newCommandId = deps.newCommandId ?? (() => crypto.randomUUID());

  /** Own-token moves in flight: commandId → move (last write per token wins
   *  in the overlay, matching server ordering semantics). */
  const pendingMoves = new Map<string, { tokenId: string; col: number; row: number }>();
  const changeListeners: Array<() => void> = [];
  const rejectionListeners: Array<(rejection: CommandRejection) => void> = [];
  const seatRemovedListeners: Array<(info: SeatRemovedInfo) => void> = [];
  /** Filled by the "seatRemoved" message that precedes the 4710 close. */
  let seatRemovedInfo: SeatRemovedInfo = { removedByName: null };
  const notify = () => {
    for (const listener of changeListeners) listener();
  };

  const room = await deps.join(options);
  status = "open";

  room.onMessage("seat", (message: SeatAssignment) => {
    seatId = message.seatId;
    deps.onSeatAssigned?.(message);
    notify();
  });
  room.onMessage("accepted", (message: { commandId: string | null }) => {
    if (message.commandId !== null && pendingMoves.delete(message.commandId)) notify();
  });
  room.onMessage("rejected", (message: CommandRejection) => {
    if (message.commandId !== null) pendingMoves.delete(message.commandId);
    for (const listener of rejectionListeners) listener(message);
    notify();
  });
  room.onMessage("seatRemoved", (message: { removedByName?: string }) => {
    seatRemovedInfo = { removedByName: message.removedByName ?? null };
  });
  room.onStateChange((schema) => {
    serverState = projectRoomState(schema.toJSON());
    notify();
  });
  room.onDrop(() => {
    status = "reconnecting";
    notify();
  });
  room.onReconnect(() => {
    status = "open";
    notify();
  });
  room.onLeave((code) => {
    status = "closed";
    if (code === SEAT_REMOVED_CLOSE_CODE) {
      for (const listener of seatRemovedListeners) listener(seatRemovedInfo);
    }
    notify();
  });

  function overlayedState(): RoomState {
    if (pendingMoves.size === 0) return serverState;
    const tokens = { ...serverState.tokens };
    for (const move of pendingMoves.values()) {
      const token = tokens[move.tokenId];
      if (token) tokens[move.tokenId] = { ...token, col: move.col, row: move.row };
    }
    return { ...serverState, tokens };
  }

  function isOwnTokenMove(command: Command): command is Command & {
    type: "moveToken";
  } {
    if (command.type !== "moveToken" || seatId === null) return false;
    return serverState.tokens[command.tokenId]?.ownerSeatId === seatId;
  }

  return {
    getState: overlayedState,
    getStatus: () => status,
    getSeatId: () => seatId,
    getPendingCommandCount: () => pendingMoves.size,
    sendCommand: (command) => {
      // CMD-09: the exact validator the server runs, no looser variant.
      const seat = seatId !== null ? (serverState.seats[seatId] ?? null) : null;
      const verdict = validateCommand(serverState, { seat }, command);
      if (!verdict.ok) {
        const rejection: CommandRejection = { commandId: null, reason: verdict.reason };
        for (const listener of rejectionListeners) listener(rejection);
        return null;
      }
      const commandId = newCommandId();
      if (isOwnTokenMove(command)) {
        pendingMoves.set(commandId, {
          tokenId: command.tokenId,
          col: command.col,
          row: command.row,
        });
        notify();
      }
      room.send("command", { commandId, command });
      return commandId;
    },
    sendRawCommand: (command) => {
      const commandId = newCommandId();
      room.send("command", { commandId, command });
      return commandId;
    },
    onChange: (listener) => changeListeners.push(listener),
    onRejection: (listener) => rejectionListeners.push(listener),
    onSeatRemoved: (listener) => seatRemovedListeners.push(listener),
    leave: async () => {
      await room.leave(true);
    },
  };
}
